import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react'
import { Layout } from './components/Layout'
import type { TabId } from './components/BottomNav'
import { ToastStack } from './components/ToastStack'
import { HideoutScreen } from './screens/HideoutScreen'
import { SurvivorsScreen } from './screens/SurvivorsScreen'
import { ExpeditionsScreen } from './screens/ExpeditionsScreen'
import { StashScreen } from './screens/StashScreen'
import { QuestsScreen } from './screens/QuestsScreen'
import { AccountScreen } from './screens/AccountScreen'
import { useI18n } from './i18n/useI18n'
import { useGameTick } from './hooks/useGameTick'
import { useAuthStore } from './store/useAuthStore'
import { useGameStore } from './store/useGameStore'
import { getPendingInitialBuildSummary } from './components/BuildWorkstationsSheet'
import { TutorialOverlay } from './components/TutorialOverlay'
const AuthScreen = lazy(() =>
  import('./screens/AuthScreen').then((m) => ({ default: m.AuthScreen })),
)
const CharacterCreationScreen = lazy(() =>
  import('./screens/CharacterCreationScreen').then((m) => ({
    default: m.CharacterCreationScreen,
  })),
)
const OnboardingScreen = lazy(() =>
  import('./screens/OnboardingScreen').then((m) => ({ default: m.OnboardingScreen })),
)
const EventModal = lazy(() =>
  import('./components/EventModal').then((m) => ({ default: m.EventModal })),
)

function SuspenseShell({ children }: { children: ReactNode }) {
  const { t } = useI18n()
  return (
    <Suspense
      fallback={
        <div className="mx-auto flex min-h-dvh max-w-[430px] items-center justify-center bg-stone-950 px-4 text-center text-sm text-stone-400">
          {t('app_loading')}
        </div>
      }
    >
      {children}
    </Suspense>
  )
}

function App() {
  const { t } = useI18n()
  const [tab, setTab] = useState<TabId>('hideout')
  const [accountOpen, setAccountOpen] = useState(false)
  const [hideoutResetKey, setHideoutResetKey] = useState(0)
  const [survivorsResetKey, setSurvivorsResetKey] = useState(0)
  const authStatus = useAuthStore((s) => s.status)
  const user = useAuthStore((s) => s.user)
  // Track which user ID has already had an initial cloud pull this session.
  // Auth token refreshes can emit a new user object (same ID) and cause the
  // effect to re-run, triggering a mid-session pull that overwrites local
  // unsynchronized state with stale cloud data.
  const pulledForUserRef = useRef<string | null>(null)
  const initializeAuth = useAuthStore((s) => s.initialize)
  const signOut = useAuthStore((s) => s.signOut)
  const expeditionCount = useGameStore((s) => s.expeditions.length)
  const inventory = useGameStore((s) => s.inventory)
  const workstations = useGameStore((s) => s.workstations)
  const hideoutTierId = useGameStore((s) => s.hideoutTierId)
  const gems = useGameStore((s) => s.gems)
  const settingsLanguage = useGameStore((s) => s.settings.language)
  const settingsTheme = useGameStore((s) => s.settings.theme)
  const onboardingStep = useGameStore((s) => s.onboardingStep)
  const starterQuest = useGameStore((s) => s.quests.starterSurvival)
  const denBoschGatewayQuest = useGameStore((s) => s.quests.denBoschGateway)
  const vehicleQuestStatus = useGameStore((s) => s.quests.vehicleQuest.status)
  const achievementsUnlocked = useGameStore((s) => s.achievementsUnlocked)
  const achievementsClaimed = useGameStore((s) => s.achievementsClaimed)
  const advanceOnboarding = useGameStore((s) => s.advanceOnboarding)
  const claimDailyLoginBonus = useGameStore((s) => s.claimDailyLoginBonus)
  const syncFromCloud = useGameStore((s) => s.syncFromCloud)
  const flushSync = useGameStore((s) => s.flushSync)
  useEffect(() => {
    void initializeAuth()
  }, [initializeAuth])

  useEffect(() => {
    document.documentElement.lang = settingsLanguage === 'en' ? 'en' : 'nl'
  }, [settingsLanguage])

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', settingsTheme)
  }, [settingsTheme])

  useEffect(() => {
    if (authStatus !== 'authenticated') return
    // Guard: only pull once per authenticated user ID. Auth token refreshes
    // can re-run this effect with the same user ID, which would trigger a
    // mid-session pull that overwrites unsynced local state.
    if (pulledForUserRef.current === user?.id) return
    pulledForUserRef.current = user?.id ?? null

    const runPull = () => {
      void syncFromCloud()
    }

    // Restore localStorage before Supabase overwrites state; otherwise unclaimed
    // trip reports merged from `prev.expeditions` are still empty on first paint.
    if (useGameStore.persist.hasHydrated()) {
      runPull()
      return
    }

    const unsub = useGameStore.persist.onFinishHydration(runPull)
    return unsub
  }, [authStatus, syncFromCloud, user?.id])

  useEffect(() => {
    if (authStatus !== 'authenticated') return
    const id = window.setInterval(() => {
      void flushSync()
    }, 5000)
    return () => window.clearInterval(id)
  }, [authStatus, flushSync])

  useEffect(() => {
    if (authStatus !== 'authenticated') return
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        void flushSync()
      }
    }
    const onBeforeUnload = () => {
      void flushSync()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [authStatus, flushSync])

  useGameTick(authStatus === 'authenticated')

  const hideoutBuildSummary = getPendingInitialBuildSummary(workstations, inventory, hideoutTierId, vehicleQuestStatus !== 'inactive')
  const hideoutDot = hideoutBuildSummary.hasAffordableBuild

  const questBadgeCount = (() => {
    let n = 0
    if (starterQuest.status === 'completed' && !starterQuest.acknowledged) n += 1
    const dg = denBoschGatewayQuest
    if (dg.status === 'available') n += 1
    if (dg.status === 'completed' && !dg.acknowledged) n += 1
    n += achievementsUnlocked.filter((id) => !achievementsClaimed.includes(id)).length
    return n
  })()

  useEffect(() => {
    if (authStatus !== 'authenticated') return
    if (onboardingStep === 'not_started') advanceOnboarding()
  }, [authStatus, onboardingStep, advanceOnboarding])

  useEffect(() => {
    if (authStatus !== 'authenticated') return
    claimDailyLoginBonus()
  }, [authStatus, claimDailyLoginBonus])

  if (authStatus === 'loading') {
    return (
      <div className="mx-auto flex min-h-dvh max-w-[430px] items-center justify-center bg-stone-950 px-4 text-center text-sm text-stone-400">
        {t('app_loading')}
      </div>
    )
  }

  if (authStatus !== 'authenticated') {
    return (
      <SuspenseShell>
        <AuthScreen />
      </SuspenseShell>
    )
  }

  if (onboardingStep === 'character_create') {
    return (
      <SuspenseShell>
        <CharacterCreationScreen />
      </SuspenseShell>
    )
  }

  if (
    onboardingStep === 'intro' ||
    onboardingStep === 'survivor_intro_1' ||
    onboardingStep === 'survivor_intro_2' ||
    onboardingStep === 'survivor_intro_3'
  ) {
    return (
      <SuspenseShell>
        <OnboardingScreen />
      </SuspenseShell>
    )
  }

  return (
    <>
      <Layout
        active={tab}
        onChange={(next) => {
          setAccountOpen(false)
          if (next === tab && !accountOpen) {
            if (next === 'hideout') setHideoutResetKey((k) => k + 1)
            if (next === 'survivors') setSurvivorsResetKey((k) => k + 1)
          }
          setTab(next)
        }}
        badges={{
          expeditions: expeditionCount,
          quests: questBadgeCount,
        }}
        dots={{
          hideout: hideoutDot,
        }}
        hideBottomNav={accountOpen}
        motionKey={accountOpen ? 'account' : tab}
        headerRight={
          accountOpen ? null : (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0.5 text-[11px] font-medium text-purple-400" title={t('gems_label')}>
                <span>◆</span>
                <span>{gems}</span>
              </div>
              <button
                type="button"
                onClick={() => setAccountOpen(true)}
                className="rounded border border-stone-700 px-2 py-1 text-[10px] uppercase tracking-wider text-stone-300 transition hover:border-amber-600 hover:text-amber-300"
                title={t('account_btn_title')}
              >
                {t('account_header_btn')}
              </button>
            </div>
          )
        }
      >
        {accountOpen ? (
          <AccountScreen
            userEmail={user?.email}
            onBack={() => setAccountOpen(false)}
            onSignOut={async () => {
              await flushSync()
              await signOut()
              setAccountOpen(false)
            }}
          />
        ) : (
          <>
            {tab === 'hideout' && <HideoutScreen forceReset={hideoutResetKey} />}
            {tab === 'survivors' && <SurvivorsScreen forceReset={survivorsResetKey} />}
            {tab === 'expeditions' && <ExpeditionsScreen />}
            {tab === 'stash' && <StashScreen />}
            {tab === 'quests' && <QuestsScreen />}
          </>
        )}
      </Layout>

      <Suspense fallback={null}>
        <EventModal />
      </Suspense>
      <ToastStack />
      <TutorialOverlay setTab={setTab} />
    </>
  )
}

export default App
